// EkranPro — web player.
// Framework-free so it runs smoothly on low-power Android TV boxes (CLAUDE.md §2).
//
// Priority of what's shown (highest wins): broadcast > playlist > idle.
// A paired screen keeps its deviceToken in localStorage and reconnects without
// re-pairing — the server remembers it until the panel removes the screen.
import './style.css';
import { io, type Socket } from 'socket.io-client';
import type {
  ServerToClientEvents,
  ClientToServerEvents,
  ResolvedBroadcast,
  PlayerPlaylist,
  ResolvedPlaylistItem,
  ResolvedMedia,
  ScheduleRule,
  Weekday,
  MenuContent,
  PairingChallenge,
  ScreenZone,
} from '@signage/shared';
import { getMenuTemplate, menuFontStack } from '@signage/shared';
import { t as tr, applyDir } from './i18n';

// Signal the index.html diagnostic that the modern bundle is executing, so its
// "could not start" fallback (for old/incapable browsers) does not fire.
(window as unknown as { __ekranpro_booted?: boolean }).__ekranpro_booted = true;

const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:4000';
// Optional ?id=<slot> lets you run several independent screens in one browser
// (each tab keeps its own deviceToken). Without it, all tabs share one screen.
const screenSlot = new URLSearchParams(location.search).get('id') ?? '';
// Marketing demo (?demo=1): runs a self-contained, looping showcase with NO
// pairing, NO socket, NO API, NO telemetry — used by the landing page's "Hemen
// Dene" preview. Real devices never carry this flag, so the production flow
// below is completely untouched (every DEMO guard is false on a real screen).
const DEMO = new URLSearchParams(location.search).has('demo');
const DEVICE_TOKEN_KEY = `signage.deviceToken${screenSlot ? `.${screenSlot}` : ''}`;
// Last-known playlist + screen config, persisted so the player shows content
// instantly on reload — even with no internet (CLAUDE.md §2.2 offline rule).
const PLAYLIST_KEY = `signage.playlist${screenSlot ? `.${screenSlot}` : ''}`;
const CONFIG_KEY = `signage.config${screenSlot ? `.${screenSlot}` : ''}`;

type PlayerSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

// ── Service worker: offline app-shell + media cache ──────────
// Registered once; it keeps the player loadable and the last playlist's media
// playable with no network. The bundle stays light — the SW is plain JS, no dep.
let swReg: ServiceWorkerRegistration | null = null;
if (!DEMO && 'serviceWorker' in navigator) {
  navigator.serviceWorker
    .register('/sw.js')
    .then((reg) => {
      swReg = reg;
    })
    .catch(() => {
      /* SW unsupported (old WebView) — player still works online */
    });
}

/** Ask the SW to pre-fetch + cache a playlist's media so it plays offline. */
function warmMediaCache(pl: PlayerPlaylist | null): void {
  if (!pl) return;
  const urls = pl.items
    .filter((it) => it.media.type !== 'youtube')
    .map((it) => it.media.url);
  if (urls.length === 0) return;
  const sw = navigator.serviceWorker?.controller ?? swReg?.active;
  sw?.postMessage({ type: 'cache-media', urls });
}

interface State {
  connected: boolean;
  paired: boolean;
  screenName: string;
  orientation: 'landscape' | 'portrait';
  weatherCity: string;
  weather: { temp: number; code: number } | null;
  prayerCity: string;
  prayer: { timings: Record<string, string> } | null;
  wifiSsid: string;
  wifiPassword: string;
  wifiShow: boolean;
  wifiQr: string;
  currencyEnabled: boolean;
  tickerEnabled: boolean;
  tickerItems: string[];
  pairing: PairingChallenge | null;
  pairingDeadline: number;
  playlist: PlayerPlaylist | null;
  broadcast: ResolvedBroadcast | null;
  /** Subscription lock (suspended / lapsed trial) → show "Pro sürüme geçin". */
  locked: boolean;
  /** Multi-zone layout (opt-in). 'full' = single fullscreen content (default). */
  zone: ScreenZone;
}

// ── Device detection + first-run setup wizard ────────────────
// On a TV's browser we offer to install the native app (true fullscreen, auto
// boot). We can't silently install an APK (OS security), but we guide the user
// through the no-USB download+install. Detection is best-effort from the UA;
// every TV view has a "continue in browser" escape hatch.
type DeviceKind =
  | 'app' // our own Android WebView (the APK) — never show the wizard
  | 'android-tv'
  | 'android-mobile'
  | 'tizen' // Samsung
  | 'webos' // LG
  | 'tv-other'
  | 'ios'
  | 'desktop';

function detectDevice(): DeviceKind {
  const ua = navigator.userAgent;
  // Android WebView (our APK) carries "; wv)" — it's already fullscreen.
  if (/;\s*wv\)|\bwv\b/.test(ua)) return 'app';
  if (/Tizen/i.test(ua)) return 'tizen';
  if (/Web0S|WebOS|webOS|NetCast/i.test(ua)) return 'webos';
  if (/Android/i.test(ua)) {
    const tvHint =
      /\bTV\b|BRAVIA|AFT|MiBOX|MiTV|GoogleTV|Google TV|SHIELD|AOSP|CrKey|ADT-|DMM|STB|TCL|Hisense|VIDAA|AQUOS|Philips|Toshiba|Skyworth|Realme TV|Chromecast/i.test(
        ua,
      ) ||
      // Android TV's Chrome UA lacks "Mobile" (phones/tablets include it).
      !/Mobile/i.test(ua);
    return tvHint ? 'android-tv' : 'android-mobile';
  }
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
  if (/SmartTV|SMART-TV|HbbTV|DTV|AppleTV|tvOS/i.test(ua)) return 'tv-other';
  return 'desktop';
}

const deviceKind = detectDevice();
const APK_URL = 'https://panel.ekranpro.com/downloads/ekranpro-tv.apk';
const SETUP_SEEN_KEY = `signage.setupSeen${screenSlot ? `.${screenSlot}` : ''}`;
let setupDismissed = (() => {
  try {
    return localStorage.getItem(SETUP_SEEN_KEY) === '1';
  } catch {
    return false;
  }
})();

// Set when the user explicitly opens the wizard from the pairing screen
// ("📺 TV uygulamasını kur"), so it works even if auto-detection missed the TV.
let forceSetup = false;

function isTvDevice(): boolean {
  return (
    deviceKind === 'android-tv' ||
    deviceKind === 'tizen' ||
    deviceKind === 'webos' ||
    deviceKind === 'tv-other'
  );
}

/** Show the install wizard: auto on a TV's first run, or whenever forced. */
function shouldShowSetup(): boolean {
  if (state.paired) return false;
  if (forceSetup) return true;
  if (setupDismissed) return false;
  return isTvDevice();
}

function openSetup(): void {
  forceSetup = true;
  paint();
}

function dismissSetup(): void {
  forceSetup = false;
  setupDismissed = true;
  try {
    localStorage.setItem(SETUP_SEEN_KEY, '1');
  } catch {
    /* ignore */
  }
  paint();
}

const state: State = {
  connected: false,
  paired: false,
  screenName: tr('screenNameDefault'),
  orientation: 'landscape',
  weatherCity: '',
  weather: null,
  prayerCity: '',
  prayer: null,
  wifiSsid: '',
  wifiPassword: '',
  wifiShow: false,
  wifiQr: '',
  currencyEnabled: false,
  tickerEnabled: false,
  tickerItems: [],
  pairing: null,
  pairingDeadline: 0,
  playlist: null,
  broadcast: null,
  locked: false,
  zone: { mode: 'full', media: null },
};

// ── Offline state persistence ────────────────────────────────
// Persist the resolved playlist + screen config so a power-cycled screen with
// no internet still resumes its last content (paired with the SW media cache).
function persistPlaylist(pl: PlayerPlaylist | null): void {
  try {
    if (pl) localStorage.setItem(PLAYLIST_KEY, JSON.stringify(pl));
    else localStorage.removeItem(PLAYLIST_KEY);
  } catch {
    /* storage full — ignore */
  }
}
function persistConfig(): void {
  try {
    localStorage.setItem(
      CONFIG_KEY,
      JSON.stringify({
        screenName: state.screenName,
        orientation: state.orientation,
        weatherCity: state.weatherCity,
        prayerCity: state.prayerCity,
        wifiSsid: state.wifiSsid,
        wifiPassword: state.wifiPassword,
        wifiShow: state.wifiShow,
        wifiQr: state.wifiQr,
        currencyEnabled: state.currencyEnabled,
        tickerEnabled: state.tickerEnabled,
        tickerItems: state.tickerItems,
        locked: state.locked,
        zone: state.zone,
      }),
    );
  } catch {
    /* ignore */
  }
}
/** Rehydrate cached state at startup so content paints before the socket connects. */
function hydrateFromCache(): void {
  const token = localStorage.getItem(DEVICE_TOKEN_KEY);
  if (!token) return; // never paired on this device
  state.paired = true; // we have a token; assume paired until told otherwise
  try {
    const cfgRaw = localStorage.getItem(CONFIG_KEY);
    if (cfgRaw) {
      const cfg = JSON.parse(cfgRaw) as Partial<State>;
      if (cfg.screenName) state.screenName = cfg.screenName;
      if (cfg.orientation) state.orientation = cfg.orientation;
      if (typeof cfg.weatherCity === 'string') state.weatherCity = cfg.weatherCity;
      if (typeof cfg.prayerCity === 'string') state.prayerCity = cfg.prayerCity;
      if (typeof cfg.wifiSsid === 'string') state.wifiSsid = cfg.wifiSsid;
      if (typeof cfg.wifiPassword === 'string') state.wifiPassword = cfg.wifiPassword;
      if (typeof cfg.wifiShow === 'boolean') state.wifiShow = cfg.wifiShow;
      if (typeof cfg.wifiQr === 'string') state.wifiQr = cfg.wifiQr;
      if (typeof cfg.currencyEnabled === 'boolean') state.currencyEnabled = cfg.currencyEnabled;
      if (typeof cfg.tickerEnabled === 'boolean') state.tickerEnabled = cfg.tickerEnabled;
      if (Array.isArray(cfg.tickerItems)) state.tickerItems = cfg.tickerItems as string[];
      if (typeof cfg.locked === 'boolean') state.locked = cfg.locked;
      if (cfg.zone && typeof cfg.zone === 'object') state.zone = cfg.zone;
    }
    const plRaw = localStorage.getItem(PLAYLIST_KEY);
    if (plRaw) {
      const pl = JSON.parse(plRaw) as PlayerPlaylist;
      if (pl && Array.isArray(pl.items) && pl.items.length > 0) state.playlist = pl;
    }
  } catch {
    /* corrupt cache — ignore */
  }
}

let playlistIndex = 0;
let playlistTimer: ReturnType<typeof setTimeout> | null = null;
let playToken = 0;
// Tracks the playlist's last-known active state so the day-boundary re-check
// (see scheduleTick) only flips between play/idle on a genuine change.
let lastPlaylistActive: boolean | null = null;
// Signature of the currently-active daypart item set; lets the minute tick swap
// menus (breakfast→lunch→dinner) at a time boundary without a panel action.
let lastDaypartSig: string | null = null;

/** Local "YYYY-MM-DD" — schedule windows are evaluated in the screen's own time. */
function localToday(): string {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

/** True when a playlist has content AND today is within its validity window. */
function playlistActive(pl: PlayerPlaylist | null): boolean {
  if (!pl || pl.items.length === 0) return false;
  const s = pl.schedule;
  if (!s || (!s.startDate && !s.endDate)) return true;
  const today = localToday();
  if (s.startDate && today < s.startDate) return false; // not started yet
  if (s.endDate && today > s.endDate) return false; // expired
  return true;
}

// ── Dayparting: per-item time-of-day windows (evaluated in LOCAL time) ──
function hhmmToMins(t: string): number | null {
  const m = /^(\d{2}):(\d{2})$/.exec(t);
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
}

/** True when an item's daypart rule is active right now (local time + weekday). */
function itemActiveNow(rule: ScheduleRule | null): boolean {
  if (!rule) return true;
  const d = new Date();
  const iso = localToday();
  if (rule.startDate && iso < rule.startDate) return false;
  if (rule.endDate && iso > rule.endDate) return false;
  // ISO weekday: 1=Mon … 7=Sun (JS getDay is 0=Sun … 6=Sat).
  const jsDay = d.getDay();
  const weekday = (jsDay === 0 ? 7 : jsDay) as Weekday;
  if (rule.days && rule.days.length > 0 && !rule.days.includes(weekday)) return false;
  const start = rule.startTime ? hhmmToMins(rule.startTime) : null;
  const end = rule.endTime ? hhmmToMins(rule.endTime) : null;
  if (start == null && end == null) return true;
  const mins = d.getHours() * 60 + d.getMinutes();
  const s = start ?? 0;
  const e = end ?? 24 * 60;
  // Normal window [s,e); a window that wraps past midnight (s>e) matches either side.
  return s <= e ? mins >= s && mins < e : mins >= s || mins < e;
}

/** The playlist's items that are eligible to play at this moment (daypart filter). */
function currentItems(): ResolvedPlaylistItem[] {
  const pl = state.playlist;
  if (!pl) return [];
  return pl.items.filter((it) => itemActiveNow(it.schedule));
}

/** Snapshot the markers the minute tick compares against, so it only acts on change. */
function syncPlaybackMarkers(): void {
  lastPlaylistActive = playlistActive(state.playlist);
  lastDaypartSig = lastPlaylistActive
    ? currentItems().map((it) => it.order).join(',')
    : '';
}

// Re-evaluate every minute so a playlist starts/expires at its validity-window
// day boundary AND swaps dayparted items (e.g. breakfast→lunch menu) at the
// time boundary — all with no panel action. Only acts on a genuine change (and
// never interrupts a live broadcast), so steady-state playback is untouched.
setInterval(() => {
  if (state.broadcast) return;
  const active = playlistActive(state.playlist);
  const sig = active ? currentItems().map((it) => it.order).join(',') : '';
  if (active !== lastPlaylistActive || sig !== lastDaypartSig) {
    lastPlaylistActive = active;
    lastDaypartSig = sig;
    playlistIndex = 0; // daypart changed → restart from the first now-active item
    resumeContent();
  }
}, 60_000);

const root = document.getElementById('app');
if (!root) throw new Error('#app bulunamadı');

// ── Proof-of-play: record each playback, batch-upload, offline-safe ──
// Events queue in localStorage so nothing is lost if the network/power drops;
// they flush every 30s (or when ≥20 pile up) and retry on reconnect.
interface PlaybackEvent {
  source: 'playlist' | 'broadcast';
  mediaAssetId: string | null;
  playlistId: string | null;
  broadcastId: string | null;
  startedAt: string;
  durationSec: number;
}
const POP_KEY = `signage.playbacks${screenSlot ? `.${screenSlot}` : ''}`;
let popQueue: PlaybackEvent[] = ((): PlaybackEvent[] => {
  try {
    return JSON.parse(localStorage.getItem(POP_KEY) ?? '[]') as PlaybackEvent[];
  } catch {
    return [];
  }
})();
let popFlushing = false;

function savePopQueue(): void {
  try {
    localStorage.setItem(POP_KEY, JSON.stringify(popQueue.slice(-2000)));
  } catch {
    /* storage full — drop silently */
  }
}
function recordPlayback(ev: PlaybackEvent): void {
  popQueue.push(ev);
  savePopQueue();
  if (popQueue.length >= 20) void flushPlaybacks();
}
async function flushPlaybacks(): Promise<void> {
  const token = localStorage.getItem(DEVICE_TOKEN_KEY);
  if (popFlushing || popQueue.length === 0 || !token) return;
  popFlushing = true;
  const batch = popQueue.slice(0, 200);
  try {
    const res = await fetch(`${API_URL}/api/v1/device/playbacks`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-device-token': token },
      body: JSON.stringify({ events: batch }),
    });
    if (res.ok) {
      popQueue = popQueue.slice(batch.length);
      savePopQueue();
    }
  } catch {
    /* offline — keep the queue and retry later */
  } finally {
    popFlushing = false;
  }
}
setInterval(() => void flushPlaybacks(), 30_000);
window.addEventListener('online', () => void flushPlaybacks());

// ── Socket ───────────────────────────────────────────────────

const deviceToken = localStorage.getItem(DEVICE_TOKEN_KEY) ?? undefined;

const socket: PlayerSocket = io(API_URL, {
  transports: ['websocket', 'polling'],
  auth: { deviceToken },
  // Demo preview never talks to the server.
  autoConnect: !DEMO,
});

// Consecutive 401/404 results from the /device/sync poll. A SINGLE bad poll can
// be a transient blip (a deploy/restart race, a momentary store inconsistency)
// — wiping a paired screen's token on one bad response would needlessly throw it
// back to the pairing code (and looks like the screen "lost itself"). We only
// give up the device after several CONSECUTIVE failures; any success — a good
// poll OR a socket device:ready — resets the counter.
let syncAuthFailures = 0;
const SYNC_AUTH_FAIL_LIMIT = 3;

socket.on('connect', () => {
  state.connected = true;
  paint();
});
socket.on('disconnect', (reason) => {
  state.connected = false;
  paint();
  // A server-initiated disconnect (e.g. this screen was deleted from the panel)
  // does NOT auto-reconnect in socket.io. Reconnect ourselves so the server
  // immediately re-evaluates us — a deleted screen then gets a fresh pairing
  // challenge instead of replaying cached content forever.
  if (reason === 'io server disconnect') socket.connect();
});

socket.on('device:pairing', (challenge) => {
  // SAFETY NET: a screen that's ALREADY paired and still holds its token must
  // not flap to the pairing code on a single challenge. After a restart/
  // reconnect the server can momentarily fail to recognise a perfectly valid
  // token — wiping a working screen for that would be catastrophic. So while we
  // already look paired, treat a pairing challenge as a transient glitch: keep
  // playing cached content and only actually re-pair after several SUSTAINED
  // failures (shared streak with the /device/sync poll). A first-time screen
  // (no stored token) still pairs immediately.
  if (state.paired && localStorage.getItem(DEVICE_TOKEN_KEY)) {
    syncAuthFailures += 1;
    if (syncAuthFailures < SYNC_AUTH_FAIL_LIMIT) return; // ignore — keep content
  }
  syncAuthFailures = 0;
  state.paired = false;
  state.pairing = challenge;
  state.pairingDeadline = Date.now() + challenge.ttlSeconds * 1000;
  state.playlist = null;
  state.broadcast = null;
  // The token is gone/invalid — don't keep stale content cached for replay.
  persistPlaylist(null);
  stopPlaylist();
  paint();
});

socket.on('device:paired', (result) => {
  localStorage.setItem(DEVICE_TOKEN_KEY, result.deviceToken);
  // CRITICAL: also put the token on the live socket's auth, so EVERY future
  // reconnect re-sends it. Without this, a freshly-paired screen keeps re-using
  // the page-load handshake (which had NO token yet) — the next reconnect looks
  // like a brand-new device and the server shows a pairing code. THE flap.
  socket.auth = { deviceToken: result.deviceToken };
  syncAuthFailures = 0;
  state.paired = true;
  state.screenName = result.name;
  state.orientation = result.orientation;
  state.weatherCity = result.weatherCity;
  state.prayerCity = result.prayerCity;
  state.wifiSsid = result.wifiSsid;
  state.wifiPassword = result.wifiPassword;
  state.wifiShow = result.wifiShow;
  state.wifiQr = result.wifiQr;
  state.currencyEnabled = result.currencyEnabled;
  state.tickerEnabled = result.tickerEnabled;
  state.pairing = null;
  persistConfig();
  applyOrientation();
  refreshWeather();
  refreshPrayer();
  resumeContent();
  renderWifiOverlay();
  void refreshTicker();
});

socket.on('device:ready', (ready) => {
  // The server just re-confirmed this device → any poll auth-failure streak was
  // a transient blip; clear it so it never accumulates toward a false revoke.
  syncAuthFailures = 0;
  state.paired = true;
  state.screenName = ready.name;
  state.orientation = ready.orientation;
  state.weatherCity = ready.weatherCity;
  state.prayerCity = ready.prayerCity;
  state.wifiSsid = ready.wifiSsid;
  state.wifiPassword = ready.wifiPassword;
  state.wifiShow = ready.wifiShow;
  state.wifiQr = ready.wifiQr;
  state.currencyEnabled = ready.currencyEnabled;
  state.tickerEnabled = ready.tickerEnabled;
  state.zone = ready.zone ?? { mode: 'full', media: null };
  state.pairing = null;
  persistConfig();
  applyOrientation();
  refreshWeather();
  refreshPrayer();
  // On a plain reconnect the playlist is already running (a brief socket drop
  // never stops local playback) — DON'T restart it, or the video visibly jumps
  // back to the start every time the socket flaps. Only (re)start when nothing
  // is currently playing (first pair, or we'd fallen back to idle). Any real
  // playlist change arrives right after via playlist:updated (change-aware).
  if (!state.broadcast && !playlistActive(state.playlist)) resumeContent();
  renderWifiOverlay();
  renderZoneOverlay();
  void refreshTicker();
});

socket.on('screen:config', (config) => {
  state.orientation = config.orientation;
  if (config.zone) state.zone = config.zone;
  persistConfig();
  applyOrientation();
  renderZoneOverlay();
});

socket.on('playlist:updated', (playlist) => {
  const lockChanged = (playlist.locked === true) !== state.locked;
  state.locked = playlist.locked === true;
  const next = playlist.items.length > 0 ? playlist : null;
  const cur = state.playlist;
  // The server re-sends the assigned playlist on every reconnect — only treat it
  // as a real change (and restart playback) when id / updatedAt / item count
  // actually differ, so a reconnect doesn't restart the current video.
  const changed =
    (cur?.id ?? '') !== (next?.id ?? '') ||
    (cur?.updatedAt ?? '') !== (next?.updatedAt ?? '') ||
    (cur?.items.length ?? 0) !== (next?.items.length ?? 0);
  state.playlist = next;
  persistPlaylist(next);
  persistConfig();
  if (changed) {
    playlistIndex = 0;
    warmMediaCache(next);
  }
  if ((changed || lockChanged) && !state.broadcast) resumeContent();
});

socket.on('broadcast:start', (broadcast) => {
  state.broadcast = broadcast;
  stopPlaylist();
  showBroadcast();
  recordPlayback({
    source: 'broadcast',
    broadcastId: broadcast.id,
    mediaAssetId:
      broadcast.content.kind === 'media' ? broadcast.content.media.assetId : null,
    playlistId: null,
    startedAt: new Date().toISOString(),
    durationSec: broadcast.ttl.mode === 'duration' ? broadcast.ttl.seconds : 0,
  });
  socket.emit('broadcast:ack', { broadcastId: broadcast.id, screenId: '' });
});

socket.on('broadcast:stop', () => {
  state.broadcast = null;
  destroyYt(); // stop a looping YouTube broadcast before returning to content
  resumeContent();
});

// ── Remote commands (panel → screen) ─────────────────────────
// Overlays live on <body>, above #app, so they survive playlist re-renders.
function setBlackout(on: boolean): void {
  let el = document.getElementById('blackout-overlay');
  if (on && !el) {
    el = document.createElement('div');
    el.id = 'blackout-overlay';
    document.body.appendChild(el);
  } else if (!on && el) {
    el.remove();
  }
}

// ── Guest Wi-Fi QR card (cafe/restaurant overlay) ────────────
// A persistent corner card on <body> (above #app) so it survives playlist
// re-renders. The QR SVG is pre-rendered server-side, so the player needs no
// QR library and the card shows instantly — even offline (cached in config).
function renderWifiOverlay(): void {
  const show =
    state.paired && state.wifiShow && !!state.wifiSsid && !state.broadcast;
  let el = document.getElementById('wifi-overlay');
  if (!show) {
    el?.remove();
    return;
  }
  if (!el) {
    el = document.createElement('div');
    el.id = 'wifi-overlay';
    document.body.appendChild(el);
  }
  el.innerHTML = `
    <div class="wifi__card">
      ${state.wifiQr ? `<div class="wifi__qr">${state.wifiQr}</div>` : ''}
      <div class="wifi__info">
        <div class="wifi__title">${tr('wifiTitle')}</div>
        <div class="wifi__row"><span class="wifi__k">${tr('wifiNetwork')}</span><span class="wifi__v">${escapeHtml(state.wifiSsid)}</span></div>
        ${
          state.wifiPassword
            ? `<div class="wifi__row"><span class="wifi__k">${tr('wifiPassword')}</span><span class="wifi__v">${escapeHtml(state.wifiPassword)}</span></div>`
            : ''
        }
        ${state.wifiQr ? `<div class="wifi__hint">${tr('wifiScanHint')}</div>` : ''}
      </div>
    </div>`;
}

// ── Multi-zone layout (opt-in) ───────────────────────────────
// A persistent secondary zone (right sidebar or bottom strip) showing one fixed
// media, composited beside the main content. Default 'full' = nothing rendered
// (zero effect on screens that never enabled it). Gated to LANDSCAPE — the body-
// level panel + .screen inset only align cleanly without the portrait rotate.
function zoneMediaHtml(m: ResolvedMedia): string {
  if (m.type === 'image') return `<img class="zone-media" src="${m.url}" alt="" />`;
  if (m.type === 'video')
    return `<video class="zone-media" src="${m.url}" autoplay muted loop playsinline></video>`;
  if (m.type === 'menu' && m.menu) return menuView(m.menu, m.assetId);
  return ''; // youtube / unsupported → empty panel
}

function renderZoneOverlay(): void {
  const z = state.zone;
  const show =
    state.paired &&
    !state.broadcast &&
    !state.locked &&
    state.orientation === 'landscape' &&
    z.mode !== 'full' &&
    !!z.media;
  const body = document.body;
  let el = document.getElementById('zone-overlay');
  if (!show || !z.media) {
    el?.remove();
    body.classList.remove('zone-on', 'zone-sidebar', 'zone-strip');
    return;
  }
  body.classList.add('zone-on');
  body.classList.toggle('zone-sidebar', z.mode === 'sidebar');
  body.classList.toggle('zone-strip', z.mode === 'strip');
  if (!el) {
    el = document.createElement('div');
    el.id = 'zone-overlay';
    body.appendChild(el);
  }
  el.innerHTML = zoneMediaHtml(z.media);
}

// ── Bottom info-bar (currency/gold + news ticker) ────────────
// A persistent full-width scrolling bar on <body> (above #app). Items are built
// + cached SERVER-SIDE (see /device/ticker), so the player needs no CORS/XML
// handling and stays light. Cached in config so it shows instantly + offline.
function renderTickerOverlay(): void {
  const show =
    state.paired &&
    (state.currencyEnabled || state.tickerEnabled) &&
    state.tickerItems.length > 0 &&
    !state.broadcast;
  document.body.classList.toggle('has-ticker', show);
  let el = document.getElementById('ticker-overlay');
  if (!show) {
    el?.remove();
    return;
  }
  if (!el) {
    el = document.createElement('div');
    el.id = 'ticker-overlay';
    document.body.appendChild(el);
  }
  const sep = '<span class="tk__sep">•</span>';
  const seq = state.tickerItems
    .map((t) => `<span class="tk__item">${escapeHtml(t)}</span>`)
    .join(sep);
  // Duplicate the sequence so the marquee loops seamlessly (translateX -50%).
  el.innerHTML = `<div class="tk__track">${seq}${sep}${seq}${sep}</div>`;
  const track = el.querySelector<HTMLElement>('.tk__track');
  if (track) {
    const dist = track.scrollWidth / 2;
    track.style.animationDuration = `${Math.max(15, Math.round(dist / 80))}s`;
  }
}

async function refreshTicker(): Promise<void> {
  if (!state.currencyEnabled && !state.tickerEnabled) {
    state.tickerItems = [];
    renderTickerOverlay();
    return;
  }
  const token = localStorage.getItem(DEVICE_TOKEN_KEY);
  if (!token) return;
  try {
    const res = await fetch(`${API_URL}/api/v1/device/ticker`, {
      headers: { 'x-device-token': token },
    });
    if (res.ok) {
      const json = (await res.json()) as { data: { items: string[] } | null };
      if (json.data && Array.isArray(json.data.items)) {
        state.tickerItems = json.data.items;
        persistConfig();
      }
    }
  } catch {
    /* offline — keep last items, retry next tick */
  }
  renderTickerOverlay();
}
// Refresh rates/headlines a few times an hour (server caches the upstream calls).
setInterval(() => void refreshTicker(), 10 * 60_000);

function flashIdentify(): void {
  document.getElementById('identify-overlay')?.remove();
  const el = document.createElement('div');
  el.id = 'identify-overlay';
  el.innerHTML = `
    <div class="identify__card">
      <div class="identify__bolt">⚡</div>
      <div class="identify__name">${escapeHtml(state.screenName)}</div>
      <div class="identify__hint">${tr('identifyHere')}</div>
    </div>`;
  document.body.appendChild(el);
  setTimeout(() => el.remove(), 8000);
}

// Report what this screen is currently displaying (answer to a 'screenshot'
// command). Reads live content from state + the visible .media element — no
// canvas capture (keeps the player lightweight and avoids CORS taint).
function sendSnapshot(): void {
  const token = localStorage.getItem(DEVICE_TOKEN_KEY);
  if (!token) return;
  let kind: 'image' | 'video' | 'youtube' | 'text' | 'idle' = 'idle';
  let url = '';
  let label = state.screenName;
  const b = state.broadcast;
  if (b) {
    if (b.content.kind === 'media') {
      const mt = b.content.media.type as string;
      kind = mt === 'video' ? 'video' : mt === 'youtube' ? 'youtube' : 'image';
      url = b.content.media.url;
    } else {
      kind = 'text';
      label = `${b.content.text.title} ${b.content.text.body}`.trim();
    }
  } else {
    const v = root?.querySelector('video.media') as HTMLVideoElement | null;
    const i = root?.querySelector('img.media') as HTMLImageElement | null;
    if (v?.src) {
      kind = 'video';
      url = v.src;
    } else if (i?.src) {
      kind = 'image';
      url = i.src;
    } else if (root?.querySelector('#ytplayer')) {
      kind = 'youtube';
    }
  }
  void fetch(`${API_URL}/api/v1/device/screenshot`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'x-device-token': token },
    body: JSON.stringify({ kind, url, label }),
  }).catch(() => {});
}

// Auto-report "now playing" on content change so the panel's screen-health
// showcase stays fresh without the operator clicking "request snapshot".
// Throttled + reuses the lightweight sendSnapshot (no image capture).
let lastNowPlayingAt = 0;
function reportNowPlaying(): void {
  const now = Date.now();
  if (now - lastNowPlayingAt < 4000) return;
  lastNowPlayingAt = now;
  // Defer a tick so the DOM (.media element) is in place before we read it.
  setTimeout(sendSnapshot, 60);
}

socket.on('screen:command', (cmd) => {
  switch (cmd.command) {
    case 'reload':
      location.reload();
      break;
    case 'blackout':
      setBlackout(cmd.on);
      break;
    case 'pause':
      if (playlistTimer) {
        clearTimeout(playlistTimer);
        playlistTimer = null;
      }
      root?.querySelector('video')?.pause();
      break;
    case 'resume':
      setBlackout(false);
      resumeContent();
      break;
    case 'identify':
      flashIdentify();
      break;
    case 'screenshot':
      sendSnapshot();
      break;
  }
});

// ── Polling fallback (CLAUDE.md §3.1) ────────────────────────
// A safety net beside the socket: every 60s we pull the screen's full state so
// a missed broadcast:start / playlist:updated is recovered even if the socket
// silently dropped. No-op while pairing (no token).
interface DeviceSyncPayload {
  screenId: string;
  name: string;
  orientation: 'landscape' | 'portrait';
  weatherCity: string;
  prayerCity: string;
  wifiSsid: string;
  wifiPassword: string;
  wifiShow: boolean;
  wifiQr: string;
  currencyEnabled: boolean;
  tickerEnabled: boolean;
  locked: boolean;
  zone: ScreenZone;
  playlist: PlayerPlaylist | null;
  broadcast: ResolvedBroadcast | null;
}

function applySync(sync: DeviceSyncPayload): void {
  // Config drift (rename / orientation / weather city).
  let configChanged = false;
  if (sync.name && sync.name !== state.screenName) {
    state.screenName = sync.name;
    configChanged = true;
  }
  if (sync.orientation !== state.orientation) {
    state.orientation = sync.orientation;
    configChanged = true;
    applyOrientation();
  }
  if (sync.weatherCity !== state.weatherCity) {
    state.weatherCity = sync.weatherCity;
    configChanged = true;
    void refreshWeather();
  }
  if (sync.prayerCity !== state.prayerCity) {
    state.prayerCity = sync.prayerCity;
    configChanged = true;
    void refreshPrayer();
  }
  // Wi-Fi QR widget drift (ssid / password / visibility / pre-rendered QR).
  if (
    sync.wifiSsid !== state.wifiSsid ||
    sync.wifiPassword !== state.wifiPassword ||
    sync.wifiShow !== state.wifiShow ||
    sync.wifiQr !== state.wifiQr
  ) {
    state.wifiSsid = sync.wifiSsid;
    state.wifiPassword = sync.wifiPassword;
    state.wifiShow = sync.wifiShow;
    state.wifiQr = sync.wifiQr;
    configChanged = true;
    renderWifiOverlay();
  }
  // Info-bar (ticker) flag drift → refetch items / hide the bar.
  if (
    sync.currencyEnabled !== state.currencyEnabled ||
    sync.tickerEnabled !== state.tickerEnabled
  ) {
    state.currencyEnabled = sync.currencyEnabled;
    state.tickerEnabled = sync.tickerEnabled;
    configChanged = true;
    void refreshTicker();
  }
  // Subscription lock drift (trial lapsed / suspended) → flip the lock notice.
  const lockChanged = sync.locked !== state.locked;
  if (lockChanged) {
    state.locked = sync.locked;
    configChanged = true;
  }
  // Multi-zone layout drift (opt-in) → re-render the secondary zone.
  const z = sync.zone ?? { mode: 'full' as const, media: null };
  if (
    z.mode !== state.zone.mode ||
    (z.media?.assetId ?? '') !== (state.zone.media?.assetId ?? '')
  ) {
    state.zone = z;
    configChanged = true;
    renderZoneOverlay();
  }
  if (configChanged) persistConfig();

  // Playlist drift (compare by updatedAt + item count — cheap and sufficient).
  const cur = state.playlist;
  const next = sync.playlist && sync.playlist.items.length > 0 ? sync.playlist : null;
  const changed =
    (cur?.id ?? '') !== (next?.id ?? '') ||
    (cur?.updatedAt ?? '') !== (next?.updatedAt ?? '') ||
    (cur?.items.length ?? 0) !== (next?.items.length ?? 0);
  if (changed || lockChanged) {
    state.playlist = next;
    playlistIndex = 0;
    persistPlaylist(next);
    warmMediaCache(next);
  }

  // Broadcast drift.
  const curB = state.broadcast?.id ?? '';
  const nextB = sync.broadcast?.id ?? '';
  if (curB !== nextB) {
    state.broadcast = sync.broadcast;
    if (state.broadcast) {
      stopPlaylist();
      showBroadcast();
    } else {
      resumeContent();
    }
    return;
  }
  // No broadcast change: refresh playlist view if it changed and nothing higher.
  if ((changed || lockChanged) && !state.broadcast) resumeContent();
}

async function pollSync(): Promise<void> {
  const token = localStorage.getItem(DEVICE_TOKEN_KEY);
  if (!token) return;
  try {
    const res = await fetch(`${API_URL}/api/v1/device/sync`, {
      headers: { 'x-device-token': token },
    });
    // 404 = screen deleted, 401 = token revoked. But ONE such response can be a
    // transient blip (deploy/restart race) — only give up the device after
    // SYNC_AUTH_FAIL_LIMIT consecutive failures so a working screen is never
    // wiped by a single bad poll. A good response below resets the streak.
    if (res.status === 404 || res.status === 401) {
      syncAuthFailures += 1;
      if (syncAuthFailures >= SYNC_AUTH_FAIL_LIMIT) revokeDevice();
      return;
    }
    syncAuthFailures = 0;
    if (!res.ok) return;
    const json = (await res.json()) as { data: DeviceSyncPayload | null };
    if (json.data) applySync(json.data);
  } catch {
    /* offline — network error (NOT an auth failure); keep content, don't count */
  }
}

/**
 * The screen was removed server-side. Forget the token + cached content and
 * reconnect with no token so the server issues a fresh pairing code.
 */
function revokeDevice(): void {
  localStorage.removeItem(DEVICE_TOKEN_KEY);
  persistPlaylist(null);
  state.paired = false;
  state.playlist = null;
  state.broadcast = null;
  stopPlaylist();
  paint();
  socket.auth = {};
  if (socket.connected) socket.disconnect();
  socket.connect();
}
setInterval(() => void pollSync(), 60_000);
window.addEventListener('online', () => void pollSync());

// ── Controller: decide what to show ──────────────────────────

function resumeContent(): void {
  // Remember the validity + daypart state so the minute tick only acts on changes.
  if (!state.broadcast) syncPlaybackMarkers();
  if (state.broadcast) {
    showBroadcast();
  } else if (playlistActive(state.playlist)) {
    startPlaylist();
  } else if (state.paired) {
    showIdle();
  } else {
    paint();
  }
  renderWifiOverlay();
  renderTickerOverlay();
  renderZoneOverlay();
}

function paint(): void {
  // Used for connecting / pairing / idle (static views).
  if (state.broadcast) return showBroadcast();
  if (playlistActive(state.playlist)) return;
  if (!root) return;
  // First-run install wizard on a TV's browser (before pairing).
  if (shouldShowSetup()) {
    root.innerHTML = setupWizardView();
    return;
  }
  if (!state.connected && !state.paired) {
    root.innerHTML = connectingView();
  } else if (!state.paired && state.pairing) {
    root.innerHTML = pairingView();
    // pairingView() ships a "--" placeholder; fill it in straight away so the
    // countdown never sits blank between repaints and the next 1s tick.
    refreshTtl();
  } else if (state.paired) {
    root.innerHTML = state.locked ? lockView() : idleView();
  } else {
    root.innerHTML = connectingView();
  }
}

function showIdle(): void {
  if (!root) return;
  root.innerHTML = state.locked ? lockView() : idleView();
  reportNowPlaying();
}

function showBroadcast(): void {
  if (!root || !state.broadcast) return;
  root.innerHTML = broadcastView(state.broadcast);
  reportNowPlaying();
  // Looping media broadcasts keep playing until stopped.
  const b = state.broadcast;
  if (b.content.kind === 'media' && b.content.media.type === 'youtube') {
    const token = ++playToken;
    playYouTube(b.content.media.url, token, () => {}, true);
  } else {
    destroyYt(); // tear down any YT player left from a prior broadcast/playlist
    const video = root.querySelector<HTMLVideoElement>('video');
    if (video) {
      revealVideoWhenReady(video);
      video.play().catch(() => {});
    }
  }
  renderWifiOverlay(); // hidden while a broadcast/emergency is on screen
  renderTickerOverlay();
  renderZoneOverlay(); // hidden during a broadcast
}

// ── Playlist playback ────────────────────────────────────────

// ── YouTube embed (IFrame API) ───────────────────────────────
// YouTube items are NOT downloaded — we embed them via YouTube's IFrame API so
// large videos never touch our storage. The API script is fetched from YouTube's
// CDN on first use (not bundled), so the player stays light.
interface YtPlayerInstance {
  destroy(): void;
  playVideo?(): void;
  mute?(): void;
}
interface YtEvent {
  target: YtPlayerInstance;
  data: number;
}
interface YtNamespace {
  Player: new (
    el: string | HTMLElement,
    opts: {
      videoId: string;
      playerVars?: Record<string, number | string>;
      events?: {
        onReady?: (e: YtEvent) => void;
        onStateChange?: (e: YtEvent) => void;
        onError?: (e: YtEvent) => void;
      };
    },
  ) => YtPlayerInstance;
  PlayerState: { ENDED: number };
}

let ytApiReady: Promise<void> | null = null;
let ytPlayer: YtPlayerInstance | null = null;

function ytNamespace(): YtNamespace | null {
  return (window as unknown as { YT?: YtNamespace }).YT ?? null;
}

function loadYtApi(): Promise<void> {
  if (ytApiReady) return ytApiReady;
  ytApiReady = new Promise<void>((resolve) => {
    if (ytNamespace()) return resolve();
    (window as unknown as { onYouTubeIframeAPIReady?: () => void }).onYouTubeIframeAPIReady =
      () => resolve();
    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    document.head.appendChild(tag);
  });
  return ytApiReady;
}

function ytVideoId(url: string): string | null {
  const m =
    url.match(/[?&]v=([a-zA-Z0-9_-]{11})/) ??
    url.match(/youtu\.be\/([a-zA-Z0-9_-]{11})/) ??
    url.match(/^([a-zA-Z0-9_-]{11})$/);
  return m?.[1] ?? null;
}

function destroyYt(): void {
  if (ytPlayer) {
    try {
      ytPlayer.destroy();
    } catch {
      /* ignore */
    }
    ytPlayer = null;
  }
}

/** Mount a muted, chrome-less YouTube player into #ytplayer; advance on end/error. */
// `loop` (broadcasts): the clip repeats for the broadcast's lifetime instead of
// advancing. YouTube's IFrame API loops a single video only when `playlist` is
// set to that same id, so we pass both.
function playYouTube(
  url: string,
  token: number,
  advance: () => void,
  loop = false,
): void {
  const id = ytVideoId(url);
  void loadYtApi().then(() => {
    if (token !== playToken) return; // superseded while the API loaded
    const YT = ytNamespace();
    if (!YT || !id) return advance();
    destroyYt();
    ytPlayer = new YT.Player('ytplayer', {
      videoId: id,
      playerVars: {
        autoplay: 1,
        mute: 1,
        controls: 0,
        rel: 0,
        modestbranding: 1,
        playsinline: 1,
        fs: 0,
        disablekb: 1,
        iv_load_policy: 3,
        ...(loop ? { loop: 1, playlist: id } : {}),
      },
      events: {
        onReady: (e) => {
          e.target.mute?.();
          e.target.playVideo?.();
        },
        onStateChange: (e) => {
          if (!loop && e.data === YT.PlayerState.ENDED) advance();
        },
        onError: () => {
          if (!loop) advance();
        },
      },
    });
  });
}

function stopPlaylist(): void {
  if (playlistTimer) {
    clearTimeout(playlistTimer);
    playlistTimer = null;
  }
  destroyYt();
  playToken++;
}

function startPlaylist(): void {
  stopPlaylist();
  if (!playlistActive(state.playlist)) {
    showIdle();
    return;
  }
  // Nothing scheduled for this moment (all items are dayparted out) → idle.
  if (currentItems().length === 0) {
    showIdle();
    return;
  }
  if (playlistIndex >= currentItems().length) playlistIndex = 0;
  playCurrent();
}

/** Keep a <video> transparent (the black wrapper shows through) until real
 *  playback begins, then reveal it. This is what hides the big "play"/loading
 *  placeholder some kiosk WebViews paint over a buffering video — we never show
 *  the element until the 'playing' event fires, so only clean black is seen
 *  while it buffers, then the moving video appears. A safety timer reveals it
 *  regardless so a missed event can never leave the screen black forever. */
function revealVideoWhenReady(video: HTMLVideoElement): void {
  const reveal = () => video.classList.add('is-ready');
  // Already progressing (e.g. attached late) → show at once.
  if (!video.paused && video.currentTime > 0) {
    reveal();
    return;
  }
  video.addEventListener('playing', reveal, { once: true });
  video.addEventListener('timeupdate', reveal, { once: true });
  // Fallback so a video that never fires 'playing' (broken/blocked) still
  // surfaces rather than sitting black — long enough that normal buffering wins.
  window.setTimeout(reveal, 12000);
}

/** Show a playlist image with a crossfade, and only once it has FULLY decoded —
 *  so images never appear to load "in pieces" and never hard-cut. The new image
 *  is layered over whatever is on screen and faded in; the previous layer(s)
 *  stay beneath until the fade completes, then are removed. */
function showImageItem(url: string, token: number): void {
  if (!root) return;
  // Reuse the media stage if one is already up (crossfade); else create it.
  let stage = root.querySelector<HTMLElement>('.media-wrap');
  if (!stage) {
    root.innerHTML = '<div class="screen media-wrap"></div>';
    stage = root.querySelector<HTMLElement>('.media-wrap');
  }
  if (!stage) return;
  const wrap = stage;

  const img = document.createElement('img');
  img.className = 'media media--layer';
  img.alt = '';
  img.decoding = 'async';
  img.src = url;

  const mount = () => {
    // Superseded while decoding, or the stage was torn down → drop this frame.
    if (token !== playToken || !wrap.isConnected) return;
    wrap.appendChild(img);
    void img.offsetWidth; // force reflow so the 0 → 1 opacity transition runs
    img.classList.add('is-shown');
    // Once the crossfade has finished, remove everything beneath the new image
    // (older image layers, a prior video/menu) — but never a newer layer that a
    // rapid next-item may have already stacked on top.
    window.setTimeout(() => {
      if (!img.isConnected) return;
      let prev = img.previousElementSibling;
      while (prev) {
        const dead = prev;
        prev = prev.previousElementSibling;
        dead.remove();
      }
    }, 700);
  };

  // Decode off-screen first so the paint is instant and complete. Fall back to
  // load/error on engines without HTMLImageElement.decode().
  if (typeof img.decode === 'function') {
    img.decode().then(mount).catch(mount);
  } else {
    img.onload = mount;
    img.onerror = mount;
  }
}

/** Warm the very next image (fetch + decode) so its crossfade is instant. The
 *  bytes land in the SW/browser cache, so a JS reference need not be kept. */
function preloadNextImage(items: ResolvedPlaylistItem[]): void {
  if (items.length < 2) return;
  const next = items[(playlistIndex + 1) % items.length]?.media;
  if (!next || next.type !== 'image') return;
  const im = new Image();
  im.decoding = 'async';
  im.src = next.url;
  im.decode?.().catch(() => {});
}

function playCurrent(): void {
  if (!root || !state.playlist) return;
  const items = currentItems();
  if (items.length === 0) {
    stopPlaylist();
    showIdle();
    return;
  }
  if (playlistIndex >= items.length) playlistIndex = 0;
  const item = items[playlistIndex];
  if (!item) {
    playlistIndex = 0;
    return;
  }

  const token = ++playToken;
  // Images crossfade in (decoded first); everything else swaps in place.
  if (item.media.type === 'image') {
    showImageItem(item.media.url, token);
  } else {
    root.innerHTML = mediaItemView(item.media, items.length);
  }
  reportNowPlaying();
  preloadNextImage(items);
  recordPlayback({
    source: 'playlist',
    mediaAssetId: item.media.assetId,
    playlistId: state.playlist.id,
    broadcastId: null,
    startedAt: new Date().toISOString(),
    durationSec: item.durationSec,
  });

  const advance = () => {
    if (token !== playToken) return; // superseded
    // The validity window may have lapsed mid-loop → fall back to idle.
    if (!playlistActive(state.playlist)) {
      syncPlaybackMarkers();
      stopPlaylist();
      showIdle();
      return;
    }
    // Re-filter: a daypart boundary may have changed the active set mid-loop.
    const live = currentItems();
    if (live.length === 0) {
      syncPlaybackMarkers();
      stopPlaylist();
      showIdle();
      return;
    }
    playlistIndex = (playlistIndex + 1) % live.length;
    playCurrent();
  };

  if (item.media.type === 'youtube') {
    // Length is the video's own; advance on end/error. Keep a long safety net
    // in case the end event is missed (e.g. embedding disabled).
    playYouTube(item.media.url, token, advance);
    playlistTimer = setTimeout(advance, 2 * 60 * 60 * 1000);
    return;
  }
  if (item.media.type === 'video') {
    // A video plays for its OWN full length — it must NOT be cut at the item's
    // slot duration (that timer is for images only). Advance when it ends or
    // errors; keep a long safety net in case the 'ended' event is missed.
    const video = root.querySelector<HTMLVideoElement>('video');
    if (video) {
      revealVideoWhenReady(video);
      video.play().catch(() => {});
      video.onended = advance;
      video.onerror = advance;
    } else {
      advance();
    }
    playlistTimer = setTimeout(advance, 2 * 60 * 60 * 1000);
    return;
  }
  // Images: advance after the item's slot duration.
  playlistTimer = setTimeout(advance, Math.max(1, item.durationSec) * 1000);
}

// ── Orientation ──────────────────────────────────────────────
// The screen's intended mounting (landscape/portrait) is set in the panel. If
// the device's actual viewport doesn't match (e.g. a landscape-native TV hung
// vertically), rotate the whole stage 90° so content fills the screen the right
// way up. Only rotates once paired — before that we don't know the orientation,
// so the pairing code shows in the device's native viewport.
function applyOrientation(): void {
  if (!root) return;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const viewport: 'landscape' | 'portrait' = vw >= vh ? 'landscape' : 'portrait';
  if (state.paired && state.orientation !== viewport) {
    // Render the stage at swapped dimensions, then rotate about its centre so
    // the rotated bounding box exactly fills the viewport.
    root.style.width = `${vh}px`;
    root.style.height = `${vw}px`;
    root.style.transform = `translate(${(vw - vh) / 2}px, ${(vh - vw) / 2}px) rotate(90deg)`;
  } else {
    root.style.width = '';
    root.style.height = '';
    root.style.transform = '';
  }
}
window.addEventListener('resize', applyOrientation);

// ── Weather widget (idle screen) ─────────────────────────────
// Optional: when the org sets a city, show current conditions on the idle
// screen via the free, key-less Open-Meteo API. Fully offline-safe — any fetch
// failure just hides the widget; it never blocks playback.
function weatherEmoji(code: number): string {
  if (code === 0) return '☀️';
  if (code <= 3) return '⛅';
  if (code === 45 || code === 48) return '🌫️';
  if (code >= 51 && code <= 67) return '🌧️';
  if (code >= 71 && code <= 77) return '❄️';
  if (code >= 80 && code <= 82) return '🌦️';
  if (code >= 95) return '⛈️';
  return '🌡️';
}

async function fetchWeather(
  city: string,
): Promise<{ temp: number; code: number } | null> {
  try {
    const geoRes = await fetch(
      `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(city)}&count=1&language=tr&format=json`,
    );
    const geo = await geoRes.json();
    const place = geo?.results?.[0];
    if (!place) return null;
    const fRes = await fetch(
      `https://api.open-meteo.com/v1/forecast?latitude=${place.latitude}&longitude=${place.longitude}&current=temperature_2m,weather_code`,
    );
    const f = await fRes.json();
    const cur = f?.current;
    if (!cur || typeof cur.temperature_2m !== 'number') return null;
    return { temp: Math.round(cur.temperature_2m), code: Number(cur.weather_code) };
  } catch {
    return null;
  }
}

function isIdleShowing(): boolean {
  return state.paired && !state.broadcast && !playlistActive(state.playlist);
}

async function refreshWeather(): Promise<void> {
  const city = state.weatherCity.trim();
  if (!city) {
    state.weather = null;
    if (isIdleShowing()) showIdle();
    return;
  }
  state.weather = await fetchWeather(city);
  if (isIdleShowing()) showIdle();
}
setInterval(() => void refreshWeather(), 30 * 60 * 1000);

function weatherView(): string {
  if (!state.weather || !state.weatherCity) return '';
  return `
    <div class="weather">
      <span class="weather__icon">${weatherEmoji(state.weather.code)}</span>
      <span class="weather__temp">${state.weather.temp}°</span>
      <span class="weather__city">${escapeHtml(state.weatherCity)}</span>
    </div>`;
}

// ── Prayer times (municipality/hospital) ─────────────────────
// Key-less Diyanet-aligned times via the Aladhan API (method 13 = Türkiye).
// Like weather: any fetch failure just hides the widget, never blocks playback.
const PRAYER_ORDER: { key: string; label: string }[] = [
  { key: 'Fajr', label: tr('prayerFajr') },
  { key: 'Sunrise', label: tr('prayerSunrise') },
  { key: 'Dhuhr', label: tr('prayerDhuhr') },
  { key: 'Asr', label: tr('prayerAsr') },
  { key: 'Maghrib', label: tr('prayerMaghrib') },
  { key: 'Isha', label: tr('prayerIsha') },
];

async function refreshPrayer(): Promise<void> {
  const city = state.prayerCity.trim();
  if (!city) {
    state.prayer = null;
    if (isIdleShowing()) showIdle();
    return;
  }
  try {
    const res = await fetch(
      `https://api.aladhan.com/v1/timingsByCity?city=${encodeURIComponent(city)}&country=Turkey&method=13`,
    );
    const json = await res.json();
    const timings = json?.data?.timings;
    state.prayer = timings && typeof timings === 'object' ? { timings } : null;
  } catch {
    state.prayer = null;
  }
  if (isIdleShowing()) showIdle();
}
// Prayer times shift daily — refresh a few times a day (and on the daily tick).
setInterval(() => void refreshPrayer(), 6 * 60 * 60 * 1000);

/** Index of the next upcoming prayer (among the 5 obligatory) for highlighting. */
function nextPrayerIndex(timings: Record<string, string>): number {
  const now = new Date();
  const mins = now.getHours() * 60 + now.getMinutes();
  const obligatory = ['Fajr', 'Dhuhr', 'Asr', 'Maghrib', 'Isha'];
  for (const key of obligatory) {
    const t = timings[key];
    if (!t) continue;
    const parts = t.split(':');
    const h = Number(parts[0]);
    const m = Number(parts[1]);
    if (Number.isNaN(h) || Number.isNaN(m)) continue;
    if (h * 60 + m > mins) return PRAYER_ORDER.findIndex((p) => p.key === key);
  }
  return PRAYER_ORDER.findIndex((p) => p.key === 'Fajr'); // tomorrow's Fajr
}

function prayerView(): string {
  if (!state.prayer || !state.prayerCity) return '';
  const { timings } = state.prayer;
  const nextIdx = nextPrayerIndex(timings);
  const cells = PRAYER_ORDER.map((p, i) => {
    const t = (timings[p.key] ?? '').slice(0, 5);
    if (!t) return '';
    return `
      <div class="prayer__cell${i === nextIdx ? ' prayer__cell--next' : ''}">
        <span class="prayer__label">${p.label}</span>
        <span class="prayer__time">${t}</span>
      </div>`;
  }).join('');
  return `
    <div class="prayer">
      <div class="prayer__head">🕌 ${escapeHtml(state.prayerCity)} · ${tr('prayerHeading')}</div>
      <div class="prayer__row">${cells}</div>
    </div>`;
}

// ── Views ────────────────────────────────────────────────────

function statusDot(): string {
  const cls = state.connected ? 'dot dot--on' : 'dot dot--off';
  const label = state.connected ? tr('connOnline') : tr('connOffline');
  return `<span class="${cls}"></span><span class="conn-label">${label}</span>`;
}

function connectingView(): string {
  return `
    <div class="screen screen--center">
      <div class="spinner"></div>
      <p class="muted">${tr('connecting')}</p>
    </div>`;
}

function setupWizardView(): string {
  // When opened manually (forceSetup), label the button "go back"; on first-run
  // auto-show it's "continue in browser". Both dismiss the wizard.
  const skip = `<button class="wz__skip" id="setup-skip">${
    forceSetup ? tr('setupGoBack') : tr('setupContinueBrowser')
  }</button>`;

  // Samsung (Tizen) / LG (webOS) can't run an APK → browser-fullscreen tips.
  // Everything else (Android TV, or a manual open on an unknown device) gets
  // the APK card — that's what someone tapping "install the TV app" wants.
  const apkCard = deviceKind !== 'tizen' && deviceKind !== 'webos';

  if (apkCard) {
    return `
    <div class="screen screen--center wz">
      <div class="wz__title">${tr('setupTvAppTitle')}</div>
      <p class="wz__sub">${tr('setupTvAppSub')}</p>
      <a class="wz__btn" id="setup-download" href="${APK_URL}" download rel="noopener">${tr('setupDownloadApk')}</a>
      <ol class="wz__steps">
        <li>${tr('setupStepOpen')}</li>
        <li>${tr('setupStepAllow')}</li>
        <li>${tr('setupStepInstall')}</li>
      </ol>
      <p class="wz__note">${tr('setupNoteAndroidTv')}<br/>
        <span class="wz__url">${APK_URL}</span></p>
      ${skip}
    </div>`;
  }

  return `
    <div class="screen screen--center wz">
      <div class="wz__title">${tr('setupTvDetectedTitle')}</div>
      <p class="wz__sub">${tr('setupTvDetectedSub')}</p>
      <ol class="wz__steps">
        <li>${tr('setupStepRemoteOk')}</li>
        <li>${tr('setupStepAddHome')}</li>
      </ol>
      <p class="wz__note">${tr('setupNoteContinue')}</p>
      ${skip}
    </div>`;
}

function pairingView(): string {
  const code = state.pairing?.code ?? '------';
  const formatted = `${code.slice(0, 3)} ${code.slice(3)}`;
  return `
    <div class="screen screen--center pairing">
      <div class="pairing__label">${tr('pairingLabel')}</div>
      <div class="pairing__code">${formatted}</div>
      <div class="pairing__timer">${tr('pairingTimer')}</div>
      <p class="pairing__hint">${tr('pairingHint')}</p>
      <button class="pair-install" id="open-setup">${tr('pairingInstall')}</button>
      <div class="conn">${statusDot()}</div>
    </div>`;
}

function fmtDateTr(iso: string): string {
  const [y, m, d] = iso.split('-');
  return d && m && y ? `${d}.${m}.${y}` : iso;
}

/** What to tell the viewer while idle — depends on the playlist's window. */
function idleStatusText(): string {
  const pl = state.playlist;
  if (pl && pl.items.length > 0 && pl.schedule) {
    const { startDate, endDate } = pl.schedule;
    const today = localToday();
    if (startDate && today < startDate)
      return tr('idleBroadcastStartsOn', { date: fmtDateTr(startDate) });
    if (endDate && today > endDate) return tr('idleBroadcastExpired');
  }
  return pl && pl.items.length > 0
    ? tr('idlePlaylistPreparing')
    : tr('idleWaitingForBroadcast');
}

function idleView(): string {
  return `
    <div class="screen idle">
      <div class="idle__top">
        <span class="idle__name">${escapeHtml(state.screenName)}</span>
        <span class="conn">${statusDot()}</span>
      </div>
      <div class="idle__center">
        <div class="clock" id="clock">--:--:--</div>
        <div class="date" id="date"></div>
        ${weatherView()}
        ${prayerView()}
        <div class="idle__wait">${idleStatusText()}</div>
      </div>
      <button class="reset" id="reset" title="${tr('resetTitle')}">${tr('resetLabel')}</button>
    </div>`;
}

/** Full-screen subscription lock: shown when the org's trial lapsed / was
 *  suspended. Replaces all content until the plan is renewed. */
function lockView(): string {
  return `
    <div class="screen lock">
      <div class="lock__card">
        <div class="lock__badge">⚡</div>
        <div class="lock__title">${tr('subscriptionLockedTitle')}</div>
        <div class="lock__body">${tr('subscriptionLockedBody')}</div>
        <div class="lock__brand">EkranPro</div>
      </div>
      <button class="reset" id="reset" title="${tr('resetTitle')}">${tr('resetLabel')}</button>
    </div>`;
}

function mediaItemView(media: ResolvedMedia, _count: number): string {
  if (media.type === 'menu') {
    return media.menu ? menuView(media.menu, media.assetId) : '';
  }
  const inner =
    media.type === 'youtube'
      ? `<div id="ytplayer" class="media"></div>`
      : media.type === 'video'
        ? `<video src="${media.url}" autoplay muted playsinline preload="auto" class="media"></video>`
        : `<img src="${media.url}" class="media" alt="" />`;
  return `<div class="screen media-wrap">${inner}</div>`;
}

/** Render a digital menu board from its structured content + chosen template. */
function menuView(m: MenuContent, assetId: string): string {
  const t = getMenuTemplate(m.template ?? m.theme);
  const cols = m.columns === 3 ? 3 : m.columns === 1 ? 1 : 2;
  const rowMod = `menu__row--${t.divider}`;
  const sections = m.sections
    .map(
      (s) => `
      <section class="menu__section">
        <h2 class="menu__sectitle">${escapeHtml(s.title)}</h2>
        <ul class="menu__list">
          ${s.items
            .map((it) => {
              const photo = it.image
                ? `<img class="menu__photo" src="${escapeHtml(it.image)}" alt="" />`
                : '';
              return `
            <li class="menu__row ${rowMod}${it.image ? ' menu__row--ph' : ''}">
              ${photo}
              <span class="menu__name">${escapeHtml(it.name)}${
                it.desc ? `<small class="menu__desc">${escapeHtml(it.desc)}</small>` : ''
              }</span>
              <span class="menu__dots"></span>
              <span class="menu__price">${escapeHtml(it.price)}</span>
            </li>`;
            })
            .join('')}
        </ul>
      </section>`,
    )
    .join('');
  const clock = m.showClock ? `<div class="menu__clock" id="menu-clock">--:--</div>` : '';
  const qr = m.showQr
    ? `<div class="menu__qr">
         <img src="${API_URL}/api/v1/menu/${assetId}/qr.svg" alt="QR" />
         <span>${tr('menuOpenOnPhone')}</span>
       </div>`
    : '';
  const vars = `--mb-bg:${t.bg};--mb-text:${t.text};--mb-muted:${t.muted};--mb-accent:${t.accent};--mb-font:${menuFontStack(t.font)}`;
  return `
    <div class="screen menu${t.upper ? ' menu--upper' : ''}" style="${vars}">
      <div class="menu__head">
        <div class="menu__heading">
          <h1 class="menu__title">${escapeHtml(m.title)}</h1>
          ${m.subtitle ? `<p class="menu__sub">${escapeHtml(m.subtitle)}</p>` : ''}
        </div>
        ${clock}
      </div>
      <div class="menu__grid menu__grid--${cols}">${sections}</div>
      ${qr}
    </div>`;
}

function broadcastView(b: ResolvedBroadcast): string {
  if (b.content.kind === 'media') {
    const m = b.content.media;
    const inner =
      m.type === 'youtube'
        ? `<div id="ytplayer" class="media"></div>`
        : m.type === 'video'
          ? `<video src="${m.url}" autoplay muted loop playsinline class="media"></video>`
          : `<img src="${m.url}" class="media" alt="" />`;
    return `<div class="screen media-wrap">${inner}</div>`;
  }

  const { title, body, style } = b.content.text;
  const isEmergency = b.type === 'emergency';

  if (style === 'ticker' && !isEmergency) {
    return `
      <div class="screen idle">
        <div class="idle__top">
          <span class="idle__name">${escapeHtml(state.screenName)}</span>
          <span class="conn">${statusDot()}</span>
        </div>
        <div class="idle__center">
          <div class="clock" id="clock">--:--:--</div>
          <div class="date" id="date"></div>
        </div>
        <div class="ticker">
          <div class="ticker__track">
            <span>${escapeHtml(title)} — ${escapeHtml(body)}</span>
            <span>${escapeHtml(title)} — ${escapeHtml(body)}</span>
          </div>
        </div>
      </div>`;
  }

  return `
    <div class="screen screen--center ${isEmergency ? 'emergency' : 'announce'}">
      ${isEmergency ? `<div class="emergency__label">${tr('emergencyLabel')}</div>` : ''}
      <div class="card">
        <div class="card__title">${escapeHtml(title)}</div>
        <div class="card__body">${escapeHtml(body)}</div>
      </div>
    </div>`;
}

// ── Clock + pairing countdown ────────────────────────────────

function tick(): void {
  const now = new Date();
  // Menüboard header clock (if shown).
  const menuClock = document.getElementById('menu-clock');
  if (menuClock) {
    menuClock.textContent = now.toLocaleTimeString('tr-TR', {
      hour: '2-digit',
      minute: '2-digit',
    });
  }
  const clock = document.getElementById('clock');
  if (clock) {
    clock.textContent = now.toLocaleTimeString('tr-TR', { hour12: false });
    const date = document.getElementById('date');
    if (date) {
      date.textContent = now.toLocaleDateString('tr-TR', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });
    }
  }
  refreshTtl();
}

/** Write the seconds left on the pairing code into the "--" placeholder. */
function refreshTtl(): void {
  const ttl = document.getElementById('ttl');
  if (!ttl) return;
  const remaining = Math.max(
    0,
    Math.ceil((state.pairingDeadline - Date.now()) / 1000),
  );
  ttl.textContent = String(remaining);
}
setInterval(tick, 1000);

document.addEventListener('click', (e) => {
  const target = e.target as HTMLElement;
  if (target.id === 'reset') {
    localStorage.removeItem(DEVICE_TOKEN_KEY);
    location.reload();
  }
  // Open the install wizard from the pairing screen (works even if auto-
  // detection missed the TV).
  if (target.closest('#open-setup')) {
    openSetup();
  }
  // Dismiss the install wizard → back to pairing.
  if (target.closest('#setup-skip')) {
    dismissSetup();
  }
});

// ── True fullscreen (hide the browser's own URL/chrome bar) ──
// Browsers forbid auto-fullscreen without a user gesture, so we trigger it on
// ANY interaction: a tap (touch screens), a click (mouse), OR a key/remote
// button (non-touch TVs with a remote — the OK button fires keydown). On
// Android TV use the EkranPro APK instead — its WebView is already fullscreen,
// boots automatically and needs no interaction. When launched as an installed
// PWA ("Add to Home Screen"), the manifest's display:fullscreen already applies.
function goFullscreen(): void {
  const el = document.documentElement as HTMLElement & {
    webkitRequestFullscreen?: () => Promise<void>;
  };
  // In the embedded marketing demo we don't hijack fullscreen on click.
  if (DEMO || document.fullscreenElement) return;
  const req = el.requestFullscreen ?? el.webkitRequestFullscreen;
  if (req) req.call(el).catch(() => {});
}
['click', 'touchend', 'keydown'].forEach((ev) =>
  document.addEventListener(ev, goFullscreen, { passive: true }),
);

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

// ── Marketing demo showcase (?demo=1) ────────────────────────
// A looping, offline, server-free preview of what a real screen plays. Renders
// inside the REAL player chrome (same #app stage + CSS + ticker band) so it's an
// honest preview, not a mockup — only the content is illustrative. Slides are
// inline SVG data-URIs: zero asset files, instant, work with no network.
function runDemo(): void {
  if (!root) return;
  const F = "font-family='Inter,Segoe UI,Roboto,Arial,sans-serif'";
  const uri = (svg: string): string =>
    `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;

  const slides: string[] = [
    // 1) Café menu board
    `<svg xmlns='http://www.w3.org/2000/svg' width='1280' height='720' viewBox='0 0 1280 720'>
      <defs><linearGradient id='g1' x1='0' y1='0' x2='0' y2='1'><stop offset='0' stop-color='#2c1a12'/><stop offset='1' stop-color='#160d08'/></linearGradient></defs>
      <rect width='1280' height='720' fill='url(#g1)'/>
      <text x='640' y='130' ${F} font-size='64' font-weight='800' fill='#e8c98f' text-anchor='middle' letter-spacing='5'>KAHVE MENÜSÜ</text>
      <rect x='460' y='158' width='360' height='4' rx='2' fill='#e8c98f'/>
      <g ${F} font-size='42' fill='#f3e9d8'>
        <text x='340' y='270'>Espresso</text><text x='940' y='270' font-weight='700' fill='#e8c98f' text-anchor='end'>₺45</text>
        <text x='340' y='360'>Latte</text><text x='940' y='360' font-weight='700' fill='#e8c98f' text-anchor='end'>₺65</text>
        <text x='340' y='450'>Cappuccino</text><text x='940' y='450' font-weight='700' fill='#e8c98f' text-anchor='end'>₺60</text>
        <text x='340' y='540'>Türk Kahvesi</text><text x='940' y='540' font-weight='700' fill='#e8c98f' text-anchor='end'>₺50</text>
        <text x='340' y='630'>Sıcak Çikolata</text><text x='940' y='630' font-weight='700' fill='#e8c98f' text-anchor='end'>₺70</text>
      </g>
    </svg>`,
    // 2) Campaign of the day
    `<svg xmlns='http://www.w3.org/2000/svg' width='1280' height='720' viewBox='0 0 1280 720'>
      <defs><linearGradient id='g2' x1='0' y1='0' x2='1' y2='1'><stop offset='0' stop-color='#4361ee'/><stop offset='1' stop-color='#7209b7'/></linearGradient></defs>
      <rect width='1280' height='720' fill='url(#g2)'/>
      <text x='640' y='180' ${F} font-size='40' font-weight='700' fill='#dfe5ff' text-anchor='middle' letter-spacing='8'>GÜNÜN KAMPANYASI</text>
      <text x='640' y='430' ${F} font-size='170' font-weight='900' fill='#ffffff' text-anchor='middle'>2 AL 1 ÖDE</text>
      <text x='640' y='540' ${F} font-size='44' fill='#eaf0ff' text-anchor='middle'>Tüm tatlılarda · yalnızca bugüne özel</text>
    </svg>`,
    // 3) Municipality announcement
    `<svg xmlns='http://www.w3.org/2000/svg' width='1280' height='720' viewBox='0 0 1280 720'>
      <rect width='1280' height='720' fill='#0b2a5b'/>
      <rect width='1280' height='12' fill='#e63946'/>
      <text x='640' y='130' ${F} font-size='34' font-weight='700' fill='#9db8e8' text-anchor='middle' letter-spacing='4'>T.C. ÖRNEK BELEDİYESİ</text>
      <text x='640' y='250' ${F} font-size='68' font-weight='800' fill='#ffffff' text-anchor='middle'>DEĞERLİ HEMŞEHRİLERİMİZ</text>
      <g ${F} font-size='40' fill='#dbe6fb' text-anchor='middle'>
        <text x='640' y='380'>Su bakım çalışması nedeniyle 14:00–17:00 arası</text>
        <text x='640' y='445'>merkez mahallelerde su kesintisi yaşanacaktır.</text>
        <text x='640' y='560' font-size='34' fill='#9db8e8'>Anlayışınız için teşekkür ederiz.</text>
      </g>
    </svg>`,
    // 4) Restaurant daily menu
    `<svg xmlns='http://www.w3.org/2000/svg' width='1280' height='720' viewBox='0 0 1280 720'>
      <defs><linearGradient id='g4' x1='0' y1='0' x2='0' y2='1'><stop offset='0' stop-color='#1d1d1f'/><stop offset='1' stop-color='#0a0a0b'/></linearGradient></defs>
      <rect width='1280' height='720' fill='url(#g4)'/>
      <text x='640' y='130' ${F} font-size='60' font-weight='800' fill='#ffd166' text-anchor='middle' letter-spacing='3'>GÜNÜN MENÜSÜ</text>
      <g ${F} font-size='46' fill='#f2f2f2' text-anchor='middle'>
        <text x='640' y='280'>Mercimek Çorbası</text>
        <text x='640' y='360'>Izgara Köfte &amp; Pilav</text>
        <text x='640' y='440'>Mevsim Salata &amp; Ayran</text>
      </g>
      <text x='640' y='600' ${F} font-size='90' font-weight='900' fill='#ffd166' text-anchor='middle'>₺185</text>
    </svg>`,
    // 5) Retail discount
    `<svg xmlns='http://www.w3.org/2000/svg' width='1280' height='720' viewBox='0 0 1280 720'>
      <defs><linearGradient id='g5' x1='0' y1='0' x2='1' y2='1'><stop offset='0' stop-color='#e63946'/><stop offset='1' stop-color='#b5179e'/></linearGradient></defs>
      <rect width='1280' height='720' fill='url(#g5)'/>
      <text x='640' y='190' ${F} font-size='46' font-weight='700' fill='#ffe0e6' text-anchor='middle' letter-spacing='10'>SEZON SONU</text>
      <text x='640' y='450' ${F} font-size='200' font-weight='900' fill='#ffffff' text-anchor='middle'>%50</text>
      <text x='640' y='560' ${F} font-size='64' font-weight='800' fill='#ffffff' text-anchor='middle' letter-spacing='6'>İNDİRİM</text>
      <text x='640' y='640' ${F} font-size='36' fill='#ffe0e6' text-anchor='middle'>Tüm ürünlerde geçerlidir</text>
    </svg>`,
  ];

  // Demo ticker band (illustrative — no upstream calls).
  state.tickerEnabled = true;
  state.tickerItems = [
    'Hoş geldiniz!',
    'USD/TRY 32,84 ▲',
    'EUR/TRY 35,12 ▲',
    'GRAM ALTIN 2.485 ▼',
    'Bugün hava güneşli ☀ 24°',
    'EkranPro ile tüm ekranlarınız tek panelden',
  ];
  // renderTickerOverlay gates on state.paired — flip it on (in-memory only).
  state.paired = true;
  renderTickerOverlay();

  let i = 0;
  const SLIDE_MS = 5500;
  const show = (): void => {
    const n = i % slides.length;
    const svg = slides[n] ?? '';
    root.innerHTML =
      `<div class="screen media-wrap"><img src="${uri(svg)}" class="media" alt="" />` +
      `<div class="pl-badge">${n + 1} / ${slides.length}</div></div>`;
    i++;
  };
  show();
  setInterval(show, SLIDE_MS);

  const badge = document.createElement('div');
  badge.id = 'demo-badge';
  badge.textContent = 'DEMO · canlı önizleme';
  document.body.appendChild(badge);
}

// Apply text direction (rtl for Arabic) before the first paint.
applyDir();

if (DEMO) {
  // Self-contained marketing showcase — never touches pairing/socket/API.
  runDemo();
} else {
  // Paint cached content first (offline-instant), then let the socket take over.
  hydrateFromCache();
  if (state.paired) {
    applyOrientation();
    warmMediaCache(state.playlist);
    resumeContent();
    void refreshTicker();
  } else {
    paint();
  }
}
